import { DevtoolsAllowedEmails } from '@/libs/core/src/lib/types/Devtools.type';
import { ListClinic } from '@chiroup/core/types/Clinic.type';
import { useEffect, useMemo, useState } from 'react';
import { Route, Routes, useLocation, useParams } from 'react-router-dom';
import useClinic from '../../../hooks/useClinic';
import Header from '../../layout/Header';
import Integrations from '../Integrations';
import ClinicExports from './ClinicExports';
import ClinicGeneral from './ClinicGeneral';
import ClinicTabs from './ClinicTabs';
import ClinicUsers from './ClinicUsers';
import Billing from './billing/Billing';
import ClinicDebug from './debug/ClinicDebug';
import ClinicLocations from './locations/ClinicLocations';
import Patients from './patients/Patients';
import PaymentProcessorConfig from './payment-processor/PaymentProcessorConfig';

const ClinicContainer = ({ user }: { user: any }) => {
  const [clinic, setClinic] = useState<Partial<ListClinic>>();
  const [clinicUsers, setClinicUsers] = useState<any>();
  const { clinicID } = useParams();
  const { data, update, refetch, isUpdating, del } = useClinic(clinicID);
  const { pathname } = useLocation();

  useEffect(() => {
    if (data) {
      const { users: nextClinicUsers, ...theClinic } = data;
      setClinic(theClinic);
      setClinicUsers(nextClinicUsers);
    }
  }, [data]);

  const hasExportAccess = useMemo(() => {
    const email = user?.signInDetails?.loginId?.toLowerCase() || user?.email?.toLowerCase() || '';
    const allowedEmails = ['kara@chiroup.com', 'email@chiroup.com', 'jeremy@chiroup.com'];

    return allowedEmails.includes(email);
  }, [user]);

  const hasDevtoolsAccess = useMemo(() => {
    // Keep gating consistent with `SidenavMenu`: prefer loginId when present and normalize casing.
    const email = user?.signInDetails?.loginId?.toLowerCase() || user?.email?.toLowerCase() || '';

    return DevtoolsAllowedEmails[email as keyof typeof DevtoolsAllowedEmails];
  }, [user]);

  return clinic ? (
    <>
      <Header title={clinic.name} />
      <ClinicTabs
        active={pathname}
        id={clinicID}
        hasExportAccess={hasExportAccess}
        hasDevtoolsAccess={hasDevtoolsAccess}
      />
      <Routes>
        <Route
          path="/"
          element={
            <ClinicGeneral
              clinic={clinic}
              update={update}
              isUpdating={isUpdating}
              del={del}
              user={user}
            />
          }
        />
        <Route
          path={`/users`}
          element={
            <ClinicUsers
              users={clinicUsers}
              clinicID={clinic.ID}
              refetch={refetch}
              clinic={clinic}
            />
          }
        />
        <Route
          path={`/locations`}
          element={
            <ClinicLocations
              locations={clinic.locations}
              clinicId={clinic.ID}
              setClinic={setClinic}
            />
          }
        />
        <Route path={`/billing`} element={<Billing clinic={clinic} />} />
        <Route path={`/integrations`} element={<Integrations clinic={clinic} />} />
        <Route
          path={`/payments`}
          element={
            <PaymentProcessorConfig clinic={{ ID: clinic.ID as number, name: clinic.name || '' }} />
          }
        />
        <Route
          path={`/patients`}
          element={hasDevtoolsAccess ? <Patients clinic={clinic} /> : null}
        />
        <Route
          path={`/debug`}
          element={hasDevtoolsAccess ? <ClinicDebug clinic={clinic} /> : null}
        />
        <Route path={`/exports`} element={<ClinicExports />} />
      </Routes>
    </>
  ) : null;
};

export default ClinicContainer;
